import React, { useState } from 'react';
import { Button, message, Modal, Upload } from 'antd';
import { CameraOutlined, UploadOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { setTemplateData } from '../../app/EditorSlice';
import { padServices } from '../../api';
import html2canvas from 'html2canvas';
import './TemplateThumbnail.css';

const TemplateThumbnail = () => {

  const dispatch = useDispatch();
  const { id } = useParams();
  const isTemplateDocument = useSelector((state) => state.editor.isTemplateDocument);
  const templateData = useSelector((state) => state.editor.templateData);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [customUrl, setCustomUrl] = useState(null);
  const existingThumbnail = templateData?.thumbnail || null;

  const handleOpenModal = () => {
    setPreviewUrl(existingThumbnail);
    setIsModalVisible(true);
  };

  const handleCapture = async () => {
    try {
      const editorContainer = document.getElementById('lexical-editor-container');
      if (!editorContainer) {
        message.error('Editor container not found');
        return;
      }

      const canvas = await html2canvas(editorContainer, {
        scale: 0.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 300,
        height: 200,
      });

      const thumbnailDataUrl = canvas.toDataURL('image/png');
      setPreviewUrl(thumbnailDataUrl);
    } catch (error) {
      console.error('Failed to capture thumbnail:', error);
      message.error('Failed to capture thumbnail');
    }
  };

  const handleConfirmUpdate = async () => {
    try {
      const thumbnailUrl = customUrl || previewUrl;
      const payload = {
        data: { thumbnail: thumbnailUrl },
        templateId: id,
        dispatch,
      };

      const res = await padServices.updateTemplate(payload);
      message.success('Thumbnail saved successfully');
      setIsModalVisible(false);
      setPreviewUrl(null);
      setCustomUrl(null);
      // Update templateData in store
      if (res?.data) {
        dispatch(setTemplateData({ ...templateData, ...res.data, thumbnail: thumbnailUrl }));
      } else {
        // Fallback: update locally
        dispatch(setTemplateData({ ...templateData, thumbnail: thumbnailUrl }));
      }
    } catch (error) {
      console.error('Failed to save thumbnail:', error);
      message.error('Failed to save thumbnail');
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setPreviewUrl(null);
    setCustomUrl(null);
  };

  const handleUpload = (info) => {
    const file = info.file;
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCustomUrl(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  if (!isTemplateDocument) return null;

  return (
    <>
      <Button
        type="text"
        icon={<CameraOutlined className="thumbnail-button-icon" />}
        onClick={handleOpenModal}
        title="Save Thumbnail"
        size="small"
        className="thumbnail-button"
      />
      <Modal
        title={<div className="thumbnail-modal-header"><CameraOutlined className="thumbnail-modal-icon" />Thumbnail Preview</div>}
        open={isModalVisible}
        onCancel={handleCancel}
        className="thumbnail-modal"
        width={560}
        footer={[
          <Button key="cancel" onClick={handleCancel} className="cancel-btn">
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={handleConfirmUpdate} className="update-thumbnail-btn">
            Save Thumbnail
          </Button>,
        ]}
      >
        <div className="thumbnail-content">
          <div className="thumbnail-preview-container" role="img" aria-label="thumbnail-preview">
            {(customUrl || previewUrl) ? (
              <img src={customUrl || previewUrl} alt="Thumbnail Preview" className="thumbnail-preview" />
            ) : (
              <div className="thumbnail-placeholder">
                <div className="placeholder-icon">📄</div>
                <p>No thumbnail yet</p>
              </div>
            )}
          </div>
          <div className="upload-section">
            <div className="upload-label">Upload a custom thumbnail</div>
            <Upload
              beforeUpload={() => false}
              onChange={handleUpload}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />} className="upload-btn">Upload Custom Thumbnail</Button>
            </Upload>
            <div className="capture-label">Capture current view</div>
            <Button icon={<CameraOutlined />} onClick={handleCapture} className="capture-btn">
              Capture Current View
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TemplateThumbnail;